import type { Token } from "@/domains/core"
import type { Config } from "@wagmi/core"
import type { Address } from "viem"
import { useMemo } from "react"
import { factoryAbi } from "@/domains/abis"
import { useQuery } from "@tanstack/react-query"
import { multicall } from "@wagmi/core"
import { formatUnits, zeroAddress } from "viem"

import type { MulticallContractConfig } from "@/domains/on-chain"
import { BIGINT_ZERO } from "@/domains/on-chain"

type Props = {
  account: Address
  token0: Token
  token1: Token
  chainId: number
  config: Config
  isStable: boolean
  routerAddress: Address
  pairFactoryAddress: Address
  refetchInterval?: number
}

export const makeWeiReturnObject = (wei: bigint, decimals: number) => {
  const value = wei ? formatUnits(wei, decimals) : "0"
  return { wei: wei ?? BIGINT_ZERO, value }
}

const minimalTokenAbi = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "_owner", type: "address" },
      { name: "_spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "remaining", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
]

export type CreateLiquidityPoolResult = {
  token0: { wei: bigint; value: string }
  token1: { wei: bigint; value: string }
  routerAllowanceToken0: { wei: bigint; value: string }
  routerAllowanceToken1: { wei: bigint; value: string }
  pairExists: boolean
  pairExistsAddress?: Address
  newPairSymbol: string
}

export const useCreateLiquidityPoolLens = ({
  account,
  chainId,
  config,
  token0,
  token1,
  isStable,
  pairFactoryAddress,
  routerAddress,
  refetchInterval = 5 * 1000,
}: Props) => {
  const createLiquidityPoolLensQuery = useQuery({
    queryKey: [token0.address, token1.address, isStable],
    queryFn: async () => {
      const contracts: MulticallContractConfig[] = [
        {
          address: token0.address,
          abi: minimalTokenAbi,
          functionName: "balanceOf",
          args: [account],
          chainId,
        },
        {
          address: token1.address,
          abi: minimalTokenAbi,
          functionName: "balanceOf",
          args: [account],
          chainId,
        },
        // check router allowances for both tokens
        {
          address: token0.address,
          abi: minimalTokenAbi,
          functionName: "allowance",
          args: [account, routerAddress],
          chainId,
        },
        {
          address: token1.address,
          abi: minimalTokenAbi,
          functionName: "allowance",
          args: [account, routerAddress],
          chainId,
        },
        {
          address: pairFactoryAddress,
          abi: factoryAbi,
          functionName: "getPair",
          args: [token0.address, token1.address, isStable],
          chainId,
        },
      ] as const

      // @ts-expect-error meh typing..
      const lens = await multicall(config, { contracts })

      return {
        token0: makeWeiReturnObject(lens[0]?.result as bigint, token0.decimals),
        token1: makeWeiReturnObject(lens[1]?.result as bigint, token1.decimals),
        routerAllowanceToken0: makeWeiReturnObject(lens[2]?.result as bigint, token0.decimals),
        routerAllowanceToken1: makeWeiReturnObject(lens[3]?.result as bigint, token1.decimals),
        pairExists: lens[4]?.status === "success" && lens[4]?.result !== zeroAddress,
        pairExistsAddress: lens[4]?.result as Address,
        newPairSymbol: makeSymbol(token0, token1),
      }
    },
    enabled: !!account,
    refetchInterval,
    refetchOnWindowFocus: false,
  })

  const lens = useMemo(() => {
    return createLiquidityPoolLensQuery.data
  }, [createLiquidityPoolLensQuery.data])

  return { createLiquidityPoolLensQuery, lens }
}

export const makeSymbol = (token0: Token, token1: Token) => {
  if (!token0 || !token1) {
    return ""
  }
  return `${token0.symbol}/${token1.symbol}`
}
